import shaders from './stock-chart.wgsl?raw';

// ============================================================================
// STOCK CHART CANDLE DATA
// ============================================================================

interface Candle {
  x: number;      // X position
  y: number;      // Y position
  z: number;      // Z position (depth)
  open: number;   // Open price (body bottom)
  close: number;  // Close price (body top)
  high: number;   // High price (wick top)
  low: number;    // Low price (wick bottom)
  width: number;  // Candle width
  isGreen: number; // 1 if bullish (close > open), 0 if bearish
  opacity: number; // For fade in/out effect
}

// ============================================================================
// STOCK CHART RENDERER
// ============================================================================

export class StockChartRenderer {
  // Device and context
  private device: GPUDevice | null = null;
  private context: GPUCanvasContext | null = null;
  private canvas: HTMLCanvasElement | null = null;

  // Candlestick data
  private candles: Candle[] = [];
  private candleCount = 150; // Increased to ensure they reach the left edge of wide screens
  private chartStreams: {
    y: number;  // Vertical position of this stream
    z: number;  // Depth position
    nextX: number; // The X position where the next candle should spawn
    lastClose: number;     // For random walk continuity
    spawnInterval: number; // Calculated based on speed and spacing
    lastSpawnTime: number; // Time of last spawn
    candleIndices: number[]; // Indices of candles belonging to this stream
    nextSpawnIndex: number; // Ring buffer index
  }[] = [];

  // Buffers
  private candleBuffer: GPUBuffer | null = null;
  private uniformBuffer: GPUBuffer | null = null;
  private instanceBuffer: GPUBuffer | null = null;

  // Pipeline
  private pipeline: GPURenderPipeline | null = null;
  private bindGroup: GPUBindGroup | null = null;

  // State
  private isInitialized = false;
  private animationFrameId: number | null = null;
  private lastTime = 0;
  private presentationFormat: GPUTextureFormat = 'bgra8unorm';
  private isInitializing = false;

  // Callbacks
  private onError: (error: string) => void;
  private onWebGLFallback: (() => void) | null;

  constructor(onError: (error: string) => void, fallbackCallback?: () => void) {
    this.onError = onError;
    this.onWebGLFallback = fallbackCallback || null;
  }

  public async initialize(canvas: HTMLCanvasElement): Promise<boolean> {
    // Prevent multiple simultaneous initializations
    if (this.isInitializing || this.isInitialized) {
      console.log('Stock chart already initialized or initialization in progress');
      return this.isInitialized;
    }

    this.isInitializing = true;
    this.canvas = canvas;

    // Check for WebGPU support
    const gpu = navigator.gpu;
    if (!gpu) {
      console.log('WebGPU not supported, falling back to WebGL');
      this.onWebGLFallback?.();
      this.isInitializing = false;
      return false;
    }

    try {
      // Request adapter
      const adapter = await gpu.requestAdapter({
        powerPreference: 'high-performance',
      });

      if (!adapter) {
        throw new Error('Failed to get GPU adapter');
      }

      // Request device
      this.device = await adapter.requestDevice();

      if (!this.device) {
        throw new Error('Failed to get GPU device');
      }

      // Get canvas context
      this.context = canvas.getContext('webgpu') as unknown as GPUCanvasContext;

      if (!this.context) {
        throw new Error('Failed to get WebGPU context');
      }

      // Get presentation format
      this.presentationFormat = gpu.getPreferredCanvasFormat();

      // Configure context
      this.context.configure({
        device: this.device,
        format: this.presentationFormat,
        alphaMode: 'premultiplied',
      });

      // Initialize candlestick charts
      this.initializeCharts();

      // Create geometry
      this.createCandleGeometry();

      // Create pipeline
      this.createPipeline();

      // Start animation
      this.isInitialized = true;
      this.startAnimation();

      console.log('Stock chart renderer initialized successfully');
      this.isInitializing = false;
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Stock chart initialization failed:', error);
      this.onError(`Stock chart initialization failed: ${errorMessage}`);
      this.cleanup();
      this.onWebGLFallback?.();
      this.isInitializing = false;
      return false;
    }
  }

  private initializeCharts(): void {
    this.candles = [];
    this.chartStreams = [];

    const numStreams = 5;
    const candlesPerStream = Math.floor(this.candleCount / numStreams);
    const spacing = 0.06; // Horizontal space between candles

    for (let s = 0; s < numStreams; s++) {
      const streamY = (Math.random() * 2 - 1) * 1.0;
      // Stagger depths significantly (farthest first to ensure proper alpha blending)
      const streamZ = -(numStreams - 1 - s) * 2.0;
      
      // Calculate spawn interval based on fixed spacing and an arbitrary "speed"
      // Decreased speed for a slower, more deliberate chart generation pacing
      const visualSpeed = 0.15 + Math.random() * 0.2;
      const spawnInterval = spacing / visualSpeed;

      // Start price
      const startPrice = 100 + (Math.random() - 0.5) * 50;

      const candleIndices: number[] = [];
      const streamStartIndex = this.candles.length;

      // Initialize all candles for this stream as hidden (opacity = 0)
      for (let c = 0; c < candlesPerStream; c++) {
        candleIndices.push(streamStartIndex + c);
        
        const candle: Candle = {
          x: -2.5, // Offscreen left
          y: streamY,
          z: streamZ + (c * 0.001), // Micro-offset to prevent Z-fighting with adjacent candles
          open: 0,
          close: 0,
          high: 0,
          low: 0,
          width: 0.035 + Math.random() * 0.015, // Made significantly wider
          isGreen: 1,
          opacity: -1, // Wait in hidden state
        };
        this.candles.push(candle);
      }

      this.chartStreams.push({
        y: streamY,
        z: streamZ,
        nextX: -2.5, // Start plotting from the far left
        lastClose: startPrice,
        spawnInterval,
        // Stagger initial spawn times so they don't all appear at exactly 0s
        lastSpawnTime: -(Math.random() * spawnInterval * 5),
        candleIndices,
        nextSpawnIndex: 0,
      });
    }
  }

  private createCandleGeometry(): void {
    if (!this.device) return;

    // Create a single box geometry for candle bodies
    // 8 vertices for a cube (2 triangles per face * 6 faces = 12 triangles = 36 vertices with normals)
    // Simplified: We'll use instancing with a unit cube

    // 72 vertices: 36 for body, 36 for wick
    const vertices = new Float32Array(72 * 3);
    const boxVerts = [
      // Front face
      -0.5, -0.5,  0.5,
       0.5, -0.5,  0.5,
       0.5,  0.5,  0.5,
      -0.5,  0.5,  0.5,
      // Back face
      -0.5, -0.5, -0.5,
      -0.5,  0.5, -0.5,
       0.5,  0.5, -0.5,
       0.5, -0.5, -0.5,
      // Top face
      -0.5,  0.5, -0.5,
      -0.5,  0.5,  0.5,
       0.5,  0.5,  0.5,
       0.5,  0.5, -0.5,
      // Bottom face
      -0.5, -0.5, -0.5,
       0.5, -0.5, -0.5,
       0.5, -0.5,  0.5,
      -0.5, -0.5,  0.5,
      // Right face
       0.5, -0.5, -0.5,
       0.5,  0.5, -0.5,
       0.5,  0.5,  0.5,
       0.5, -0.5,  0.5,
      // Left face
      -0.5, -0.5, -0.5,
      -0.5, -0.5,  0.5,
      -0.5,  0.5,  0.5,
      -0.5,  0.5, -0.5,
    ];

    for (let i = 0; i < 24; i++) {
        // Body (first 24 vertices)
        vertices[i * 3 + 0] = boxVerts[i * 3 + 0];
        vertices[i * 3 + 1] = boxVerts[i * 3 + 1];
        vertices[i * 3 + 2] = boxVerts[i * 3 + 2];
        
        // Wick (second 24 vertices)
        vertices[(i + 24) * 3 + 0] = boxVerts[i * 3 + 0];
        vertices[(i + 24) * 3 + 1] = boxVerts[i * 3 + 1];
        vertices[(i + 24) * 3 + 2] = boxVerts[i * 3 + 2];
    }

    const baseIndices = [
      0,  1,  2,  0,  2,  3,  // Front
      4,  5,  6,  4,  6,  7,  // Back
      8,  9,  10, 8,  10, 11, // Top
      12, 13, 14, 12, 14, 15, // Bottom
      16, 17, 18, 16, 18, 19, // Right
      20, 21, 22, 20, 22, 23, // Left
    ];

    const indices = new Uint16Array(72);
    // Body indices
    for (let i = 0; i < 36; i++) {
        indices[i] = baseIndices[i];
    }
    // Wick indices (offset by 24 for the 24 new vertices)
    for (let i = 0; i < 36; i++) {
        indices[i + 36] = baseIndices[i] + 24;
    }

    // Create vertex buffer
    const vertexBuffer = this.device.createBuffer({
      size: vertices.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(vertexBuffer, 0, vertices);

    // Create index buffer
    const indexBuffer = this.device.createBuffer({
      size: indices.byteLength,
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(indexBuffer, 0, indices);

    // Create instance buffer (candle data)
    // Each candle: x, y, z, open, close, high, low, width, isGreen, opacity (10 floats)
    const instanceData = new Float32Array(this.candleCount * 10);
    for (let i = 0; i < this.candleCount; i++) {
      const c = this.candles[i];
      const offset = i * 10;
      instanceData[offset + 0] = c.x;
      instanceData[offset + 1] = c.y;
      instanceData[offset + 2] = c.z;
      instanceData[offset + 3] = c.open;
      instanceData[offset + 4] = c.close;
      instanceData[offset + 5] = c.high;
      instanceData[offset + 6] = c.low;
      instanceData[offset + 7] = c.width;
      instanceData[offset + 8] = c.isGreen;
      instanceData[offset + 9] = c.opacity;
    }

    this.instanceBuffer = this.device.createBuffer({
      size: instanceData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.instanceBuffer, 0, instanceData);

    // Uniform buffer: viewProj (64 bytes) + time (4 bytes) + padding (12 bytes) = 80 bytes
    this.uniformBuffer = this.device.createBuffer({
      size: 80,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  private createPipeline(): void {
    if (!this.device) return;

    const shaderModule = this.device.createShaderModule({ code: shaders });

    this.pipeline = this.device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: shaderModule,
        entryPoint: 'vertex_main',
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fragment_main',
        targets: [
          {
            format: this.presentationFormat,
            blend: {
              color: {
                srcFactor: 'src-alpha',
                dstFactor: 'one-minus-src-alpha',
                operation: 'add',
              },
              alpha: {
                srcFactor: 'one',
                dstFactor: 'one-minus-src-alpha',
                operation: 'add',
              },
            },
          },
        ],
      },
      primitive: {
        topology: 'triangle-list',
      },
      depthStencil: {
        format: 'depth24plus',
        depthWriteEnabled: true,
        depthCompare: 'less',
      },
    });

    const layout = this.pipeline.getBindGroupLayout(0);

    this.bindGroup = this.device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: { buffer: this.uniformBuffer! } },
        { binding: 1, resource: { buffer: this.instanceBuffer! } },
      ],
    });
  }

  private updateUniforms(): void {
    if (!this.device || !this.uniformBuffer || !this.canvas) return;

    const aspectRatio = this.canvas.width / this.canvas.height;

    // Perspective projection matrix
    const fov = Math.PI / 4; // 45 degrees
    const near = 0.1;
    const far = 100.0;
    const f = 1.0 / Math.tan(fov / 2);

    const projMatrix = new Float32Array([
      f / aspectRatio, 0.0, 0.0, 0.0,
      0.0, f, 0.0, 0.0,
      0.0, 0.0, (far + near) / (near - far), -1.0,
      0.0, 0.0, (2 * far * near) / (near - far), 0.0,
    ]);

    // View matrix (camera looking from front)
    const viewMatrix = new Float32Array([
      1.0, 0.0, 0.0, 0.0,
      0.0, 1.0, 0.0, 0.0,
      0.0, 0.0, 1.0, 0.0,
      0.0, 0.0, -5.0, 1.0,
    ]);

    // Combine view and projection
    const viewProj = new Float32Array(16);
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        viewProj[row * 4 + col] =
          viewMatrix[row * 4 + 0] * projMatrix[0 * 4 + col] +
          viewMatrix[row * 4 + 1] * projMatrix[1 * 4 + col] +
          viewMatrix[row * 4 + 2] * projMatrix[2 * 4 + col] +
          viewMatrix[row * 4 + 3] * projMatrix[3 * 4 + col];
      }
    }

    const time = performance.now() / 1000;

    const data = new Float32Array(20);
    data.set(viewProj, 0);
    data[16] = time;
    data[17] = 0.0;
    data[18] = 0.0;
    data[19] = 0.0;

    this.device.queue.writeBuffer(this.uniformBuffer, 0, data);
  }

  public update(): void {
    if (!this.isInitialized || !this.device || !this.instanceBuffer) return;

    const deltaTime = Math.min(0.1, (performance.now() - this.lastTime) / 1000);
    this.lastTime = performance.now();

    const time = performance.now() / 1000;

    // Handle spawning new candles for streams
    for (const stream of this.chartStreams) {
      // Is it time to spawn a next static candle in the series?
      if (time - stream.lastSpawnTime >= stream.spawnInterval) {
        stream.lastSpawnTime = time;

        // If the series reached the right edge, reset it entirely
        if (stream.nextX > 3.0) {
          stream.nextX = -2.5; // Back to left edge
          stream.lastClose = 100 + (Math.random() - 0.5) * 50; // New random start price
          
          // Hide all previously drawn candles for this stream to simulate a fresh screen
          // Use -1 to wait in hidden state without triggering fade-in
          for (const globalIndex of stream.candleIndices) {
            this.candles[globalIndex].opacity = -1;
          }
        }

        // Get the next candle in the ring buffer for this stream
        const globalIndex = stream.candleIndices[stream.nextSpawnIndex];
        const candle = this.candles[globalIndex];

        // Generate next random walk price (next open is near previous close)
        // Occasional gaps allowed, but mostly contiguous
        const gap = (Math.random() > 0.8) ? (Math.random() - 0.5) * 4.0 : 0; // Increased gap size
        const openPrice = stream.lastClose + gap;
        
        // Random price movement for close
        const move = (Math.random() - 0.45) * 15.0; // Increased price volatility
        const closePrice = openPrice + move;
        const isGreen = closePrice >= openPrice;
        
        const highPrice = Math.max(openPrice, closePrice) + Math.random() * 8; // Increased wick length
        const lowPrice = Math.min(openPrice, closePrice) - Math.random() * 6; // Increased wick length

        // Update stream last close
        stream.lastClose = closePrice;

        // Plot the candle exactly at the current cursor position (nextX)
        candle.x = stream.nextX;

        // Taller candles 
        candle.open = (openPrice * 0.015 - 1.5) * 3.5;
        candle.close = (closePrice * 0.015 - 1.5) * 3.5;
        candle.high = (highPrice * 0.015 - 1.5) * 3.5;
        candle.low = (lowPrice * 0.015 - 1.5) * 3.5;
        candle.isGreen = isGreen ? 1 : 0;
        candle.width = 0.035 + Math.random() * 0.015; // Generate new width per candle
        candle.opacity = 0.0; // Trigger fade in

        // Advance ring buffer and X cursor position for next tick
        stream.nextSpawnIndex = (stream.nextSpawnIndex + 1) % stream.candleIndices.length;
        
        // Calculate dynamic spacing to strictly prevent overlaps based on actual instance width
        // instance width in shader is scaled by 10.0, so visual width is candle.width * 10.0
        // We need to space nextX by at least half the current width + half the next width + padding
        // Since we don't know the next width yet, we step by the full width of the current object + small gap
        const visualWidth = candle.width * 10.0;
        const spacingPadding = 0.02;
        stream.nextX += visualWidth + spacingPadding; 
      }
    }

    // Update active candle positions
    const instanceData = new Float32Array(this.candleCount * 10);
    for (let i = 0; i < this.candleCount; i++) {
        const candle = this.candles[i];

        // Fade in new candles quickly
        if (candle.opacity >= 0 && candle.opacity < 1) {
            candle.opacity = Math.min(1.0, candle.opacity + deltaTime * 5);
        }

        // Remove wave and movement logic completely
        const offset = i * 10;
        instanceData[offset + 0] = candle.x;
        instanceData[offset + 1] = candle.y;
        instanceData[offset + 2] = candle.z;
        instanceData[offset + 3] = candle.open;
        instanceData[offset + 4] = candle.close;
        instanceData[offset + 5] = candle.high;
        instanceData[offset + 6] = candle.low;
        instanceData[offset + 7] = candle.width;
        instanceData[offset + 8] = candle.isGreen;
        instanceData[offset + 9] = candle.opacity > 0 ? candle.opacity : 0;
    }

    this.device.queue.writeBuffer(this.instanceBuffer, 0, instanceData);
  }

  public render(): void {
    if (!this.isInitialized || !this.device || !this.context) return;

    this.updateUniforms();

    const encoder = this.device.createCommandEncoder();
    const textureView = this.context.getCurrentTexture().createView();

    const depthTexture = this.device.createTexture({
      size: [this.canvas!.width, this.canvas!.height],
      format: 'depth24plus',
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });

    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: textureView,
          clearValue: { r: 0.02, g: 0.0, b: 0.05, a: 1.0 }, // NGE purple background
          loadOp: 'clear',
          storeOp: 'store',
        },
      ],
      depthStencilAttachment: {
        view: depthTexture.createView(),
        depthClearValue: 1.0,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
      },
    });

    pass.setPipeline(this.pipeline!);
    pass.setBindGroup(0, this.bindGroup!);

    // Draw instanced cubes (72 vertices per cube: 36 for body + 36 for wick)
    // @ts-ignore: type definitions conflict for draw arguments
    pass.draw(72, this.candleCount);

    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  private startAnimation(): void {
    if (this.animationFrameId !== null) {
      console.log('Animation already running');
      return;
    }

    this.lastTime = performance.now();

    const animate = () => {
      if (!this.isInitialized || !this.device || !this.context) {
        console.log('Renderer no longer initialized, stopping animation');
        this.animationFrameId = null;
        return;
      }

      this.update();
      this.render();
      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  public cleanup(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.candleBuffer?.destroy();
    this.uniformBuffer?.destroy();
    this.instanceBuffer?.destroy();

    this.isInitialized = false;
    this.isInitializing = false;
    this.device = null;
    this.canvas = null;
  }
}
